import { createStateHook, type HookStateFunction } from 'react-sweet-state';

import { PARENT_FIELD_ID } from '../../../common/constants';
import type { DraftWorkItem, DraftWorkItemWithHierarchy } from '../../../common/types';
import { isIssueKey } from '../../../utils/isIssueKey';
import { getMessageState, Store } from '../index';
import type { DraftWorkItemsState } from '../types';

export const useAreAllCreated: HookStateFunction<boolean, string> = createStateHook(Store, {
	selector: (state: DraftWorkItemsState, messageId: string) => {
		const { isInitialised, draftWorkItemsMap } = getMessageState(state, messageId);
		if (!isInitialised) {
			return false;
		}
		return Object.values(draftWorkItemsMap)
			.filter((item) => item.status !== 'cancelled')
			.every((item) => item.createdIssueKey);
	},
});

export const useAreAllDismissed: HookStateFunction<boolean, string> = createStateHook(Store, {
	selector: (state: DraftWorkItemsState, messageId: string) => {
		const { isInitialised, draftWorkItemsMap } = getMessageState(state, messageId);
		if (!isInitialised) {
			return false;
		}
		return Object.values(draftWorkItemsMap).every((item) => item.status === 'cancelled');
	},
});

export const useGetDraftWorkItemsWithHierarchy: HookStateFunction<
	DraftWorkItemWithHierarchy[],
	string
> = createStateHook(Store, {
	selector: (state: DraftWorkItemsState, messageId: string) => {
		const { draftWorkItemsMap, topLevelInvocationIds } = getMessageState(state, messageId);
		const draftWorkItemsWithHierarchy: DraftWorkItemWithHierarchy[] = [];
		topLevelInvocationIds.forEach((topLevelInvocationId) => {
			const draftWorkItem = draftWorkItemsMap[topLevelInvocationId];
			if (draftWorkItem) {
				draftWorkItemsWithHierarchy.push({
					...draftWorkItem,
					childWorkItems:
						draftWorkItem.childInvocationIds
							?.map((child) => draftWorkItemsMap[child])
							.filter((child) => child && !topLevelInvocationIds.includes(child.invocationId)) ||
						[],
				});
			}
		});
		return draftWorkItemsWithHierarchy;
	},
});

export const useGetDraftWorkItemsToCreate: HookStateFunction<DraftWorkItemWithHierarchy[], string> =
	createStateHook(Store, {
		selector: (state: DraftWorkItemsState, messageId: string) => {
			const { draftWorkItemsMap } = getMessageState(state, messageId);
			// TODO: Refactor onCreate to be able to create items without the hierarchy logic

			// Return only active (not cancelled or created) work items with Hierarchy
			const notCreatedItems = Object.values(draftWorkItemsMap).filter(
				(item) => item.status !== 'cancelled' && !item.createdIssueKey,
			);

			// Helper to build child work items with hierarchy, will only include children that are active (not cancelled or created)
			const buildChildWorkItems = (item: DraftWorkItem) =>
				item.childInvocationIds
					?.map((childId) => draftWorkItemsMap[childId])
					.filter(
						(child) => child != null && !child.createdIssueKey && child.status !== 'cancelled',
					) || [];

			const topLevelNotCreatedItems = notCreatedItems.filter(
				(item) => !item.fields[PARENT_FIELD_ID],
			);

			// Only include child that have a parent that has been created, the ones with a parent that hasn't been created will be created as top level items
			const childNotCreatedItems = notCreatedItems.filter(
				(item) =>
					typeof item.fields[PARENT_FIELD_ID] === 'string' &&
					isIssueKey(item.fields[PARENT_FIELD_ID] as string),
			);

			return [...topLevelNotCreatedItems, ...childNotCreatedItems].map((item) => ({
				...item,
				childWorkItems: buildChildWorkItems(item),
			}));
		},
	});

/**
 * For each parent work item, lists child invocation IDs that are still in `planned` status.
 * Consumers look up by parent id and default to [] when missing (no children / none planned).
 */
export const useGetActivePlannedChildInvocationIdsByParent: HookStateFunction<
	Record<string, string[]>,
	string
> = createStateHook(Store, {
	selector: (state: DraftWorkItemsState, messageId: string) => {
		const { draftWorkItemsMap } = getMessageState(state, messageId);
		const activePlannedChildInvocationIdsByParent: Record<string, string[]> = {};

		for (const parent of Object.values(draftWorkItemsMap)) {
			const { invocationId, childInvocationIds } = parent;
			if (!childInvocationIds?.length) {
				continue;
			}

			const plannedOnly = childInvocationIds.filter(
				(childId) => draftWorkItemsMap[childId]?.status === 'planned',
			);

			if (plannedOnly.length > 0) {
				activePlannedChildInvocationIdsByParent[invocationId] = plannedOnly;
			}
		}

		return activePlannedChildInvocationIdsByParent;
	},
});

export const useGetActiveOrFinishedDraftWorkItemsCount: HookStateFunction<number, string> =
	createStateHook(Store, {
		selector: (state: DraftWorkItemsState, messageId: string) => {
			const { draftWorkItemsMap } = getMessageState(state, messageId);
			return Object.values(draftWorkItemsMap).length;
		},
	});

export const useHasAppliedDraftEdits: HookStateFunction<boolean, string> = createStateHook(Store, {
	selector: (state: DraftWorkItemsState, messageId: string) =>
		getMessageState(state, messageId).hasAppliedDraftEdits,
});
